import {
  validateText, validateCountry, validateState, validatePhoneNumber,
  validateAddress, validateZipCode, validateEmail,
} from 'utils/validation.util';
import { EMAIL_REGEX } from 'constants/AppConstants';

// Validates multiple comma-separated email addresses
export const validateMultipleEmails = (emailString) => {
  if (!emailString || emailString.trim().length === 0) return true;

  const emails = emailString.split(',').map((email) => email.trim());
  return emails.every((email) => email.length === 0 || EMAIL_REGEX.test(email));
};

// #genai - Validation rules configuration for address fields
const getValidationRules = (addressInfo) => [
  { field: 'firstName', validate: () => validateText(addressInfo.firstName), errorKey: 'invalid_firstName' },
  { field: 'lastName', validate: () => validateText(addressInfo.lastName), errorKey: 'invalid_lastName' },
  { field: 'company', validate: () => validateText(addressInfo.company), errorKey: 'invalid_companyName' },
  { field: 'emailAddress', validate: () => validateEmail(addressInfo.emailAddress), errorKey: 'invalid_email' },
  { field: 'emailCC', validate: () => validateMultipleEmails(addressInfo.emailCC), errorKey: 'invalid_recipient_email' },
  { field: 'phone', validate: () => validatePhoneNumber(addressInfo.phone), errorKey: 'invalid_phone' },
  { field: 'address1', validate: () => validateAddress(addressInfo.address1), errorKey: 'invalid_address1' },
  { field: 'city', validate: () => validateText(addressInfo.city), errorKey: 'invalid_city' },
  { field: 'country', validate: () => validateCountry(addressInfo.country), errorKey: 'invalid_country' },
  { field: 'state', validate: () => validateState(addressInfo.country, addressInfo.state), errorKey: 'invalid_state' },
  { field: 'zip', validate: () => validateZipCode(addressInfo.country, addressInfo.zip), errorKey: 'invalid_zip' },
];

// #genai - Refactored to reduce cognitive complexity using data-driven validation
export const checkForRegexAndLengthValidations = (t, addressInfo, validationErrors) => {
  const updateValidationValue = { ...validationErrors };
  const validationRules = getValidationRules(addressInfo);

  validationRules.forEach(({ field, validate, errorKey }) => {
    const fieldValue = addressInfo?.[field];
    const hasValue = fieldValue?.length !== 0;

    if (hasValue) {
      const errorMessage = t(`contact_information__address_information__${errorKey}`);
      updateValidationValue[field] = validate() ? '' : errorMessage;
    }
  });

  return updateValidationValue;
};

export const validationandRegexErrors = (t, addressInfo) => {
  const validationErrors = {};
  if (addressInfo.firstName.length === 0) {
    validationErrors.firstName = t('contact_information__address_information__firstName_required');
  }
  if (addressInfo.lastName.length === 0) {
    validationErrors.lastName = t('contact_information__address_information__lastName_required');
  }
  if (addressInfo.company.length === 0) {
    validationErrors.company = t('contact_information__address_information__companyName_required');
  }
  if (addressInfo.emailAddress.length === 0) {
    validationErrors.emailAddress = t('contact_information__address_information__email_required');
  }
  if (addressInfo.alternateReportEmail && addressInfo.emailCC.length === 0) {
    validationErrors.emailCC = t('contact_information__address_information__email_required');
  }
  if (addressInfo.phone.length === 0) {
    validationErrors.phone = t('contact_information__address_information__phone_required');
  }
  if (addressInfo.address1.length === 0) {
    validationErrors.address1 = t('contact_information__address_information__address1_required');
  }
  if (addressInfo.city.length === 0) {
    validationErrors.city = t('contact_information__address_information__city_required');
  }
  if (addressInfo.country.length === 0) {
    validationErrors.country = t('contact_information__address_information__country_required');
  }
  if (addressInfo.state.length === 0) {
    validationErrors.state = t('contact_information__address_information__state_required');
  }
  if (addressInfo.zip.length === 0) {
    validationErrors.zip = t('contact_information__address_information__zip_required');
  }
  return checkForRegexAndLengthValidations(t, addressInfo, validationErrors);
};

export const showValidationBanner = (
  addressInfoErrors,
) => Object.values(addressInfoErrors).some((val) => val !== '');
